import type React from 'react';
import { forwardRef, memo } from 'react';
import styled from 'styled-components';

import { multilineEllipsis } from '@/styles/style';

import type { TypoWeight } from './types';
import { getWeight } from './Typo';

export interface TitleStyleProps {
  color?: string;
  weight?: TypoWeight;
  multilineEllipsis?: number;
}

export type TitleProps = React.HTMLAttributes<HTMLHeadingElement> &
  TitleStyleProps & {
    type?: 'hero' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
    children: React.ReactNode;
    weight?: TypoWeight;
  };

const Heading = (props: TitleProps, ref: React.Ref<HTMLHeadingElement>) => {
  const { type, color, weight, children, ...rest } = props;

  const weightValue = props.weight || 'sb';
  if (props.type === 'hero')
    return (
      <HeroStyle
        {...rest}
        color={color}
        style={props.style}
        weight={weightValue}
        ref={ref}
      >
        {props.children}
      </HeroStyle>
    );
  if (props.type === 'h1')
    return (
      <Header1Style
        {...rest}
        color={color}
        style={props.style}
        weight={weightValue}
        ref={ref}
      >
        {props.children}
      </Header1Style>
    );
  if (props.type === 'h2')
    return (
      <Header2Style
        {...rest}
        color={color}
        style={props.style}
        weight={weightValue}
        ref={ref}
      >
        {props.children}
      </Header2Style>
    );
  if (props.type === 'h3')
    return (
      <Header3Style
        {...rest}
        color={color}
        style={props.style}
        weight={weightValue}
        ref={ref}
      >
        {props.children}
      </Header3Style>
    );
  if (props.type === 'h4')
    return (
      <Header4Style
        {...rest}
        color={color}
        style={props.style}
        weight={weightValue}
        ref={ref}
      >
        {props.children}
      </Header4Style>
    );
  if (props.type === 'h5')
    return (
      <Header5Style
        {...rest}
        color={color}
        style={props.style}
        weight={weightValue}
        ref={ref}
      >
        {props.children}
      </Header5Style>
    );
  return (
    <Header1Style
      {...rest}
      color={color}
      style={props.style}
      weight={weightValue}
      ref={ref}
    >
      {props.children}
    </Header1Style>
  );
};

export const HeroStyle = styled.h1<TitleStyleProps>`
  margin: 0;
  font-size: 32px;
  line-height: 1.2;
  font-weight: ${({ weight }) => getWeight(weight)};
  color: ${({ color }) => color};
  ${(props) =>
    props.multilineEllipsis && multilineEllipsis(props.multilineEllipsis)};
`;

export const Header1Style = styled.h1<TitleStyleProps>`
  margin: 0;
  font-size: 28px;
  line-height: 1.2;
  font-weight: ${({ weight }) => getWeight(weight)};
  color: ${({ color }) => color};
  ${(props) =>
    props.multilineEllipsis && multilineEllipsis(props.multilineEllipsis)};
`;

export const Header2Style = styled.h2<TitleStyleProps>`
  margin: 0;
  font-size: 24px;
  line-height: 1.2;
  font-weight: ${({ weight }) => getWeight(weight)};
  color: ${({ color }) => color};
  ${(props) =>
    props.multilineEllipsis && multilineEllipsis(props.multilineEllipsis)};
`;

export const Header3Style = styled.h3<TitleStyleProps>`
  margin: 0;
  font-size: 20px;
  line-height: 1.2;
  font-weight: ${({ weight }) => getWeight(weight)};
  color: ${({ color }) => color};
  ${(props) =>
    props.multilineEllipsis && multilineEllipsis(props.multilineEllipsis)};
`;

export const Header4Style = styled.h4<TitleStyleProps>`
  margin: 0;
  font-size: 18px;
  line-height: 1.2;
  font-weight: ${({ weight }) => getWeight(weight)};
  color: ${({ color }) => color};
`;

export const Header5Style = styled.h5<TitleStyleProps>`
  margin: 0;
  font-size: 16px;
  line-height: 1.2;
  font-weight: ${({ weight }) => getWeight(weight)};
  color: ${({ color }) => color};
`;

export default memo(forwardRef(Heading));
